type RetryOptions = {
  retries?: number;
  delaysMs?: number[];
  shouldRetry?: (error: unknown) => boolean;
  onRetry?: (error: unknown, attempt: number) => void;
};

const defaultDelaysMs = [500, 1500, 3000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class EmptyJsonResponseError extends SyntaxError {
  constructor(message = "Empty JSON response") {
    super(message);
    this.name = "EmptyJsonResponseError";
  }
}

export function isTransientJsonParseError(error: unknown) {
  if (error instanceof EmptyJsonResponseError) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === "EmptyJsonResponseError" ||
    (error.name === "SyntaxError" && /unexpected eof|unexpected end of json input/i.test(error.message))
  );
}

export async function retryTransientJson<T>(
  operation: () => Promise<T>,
  {
    retries = 2,
    delaysMs = defaultDelaysMs,
    shouldRetry = isTransientJsonParseError,
    onRetry,
  }: RetryOptions = {},
) {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt >= retries || !shouldRetry(error)) {
        throw error;
      }

      onRetry?.(error, attempt + 1);
      await sleep(delaysMs[attempt] ?? delaysMs[delaysMs.length - 1]);
    }
  }

  throw new Error("Retry operation failed without returning a result");
}

export async function parseJsonResponse<T>(response: Response): Promise<T> {
  const responseText = await response.text();

  if (!responseText.trim()) {
    throw new EmptyJsonResponseError();
  }

  return JSON.parse(responseText) as T;
}
